/**
 * §12.4 synthesis recipes + §12.7 family inventory. Every one of the 48 cues in
 * `content/audio-cues.json` is synthesized here; nothing is fetched (spine §8).
 *
 * The cue table is derived from the content JSON at module load, so a new cue id in the
 * data table cannot silently go unimplemented — `missingRecipes()` reports it.
 */

import { AUDIO_CUES, type AudioCueDef } from '../core/content';
import type { AudioGraph, FamilyName } from './context';
import { FAMILY_NAMES } from './context';
import {
  attachLfo,
  bellVoice,
  disconnectVoice,
  EPS,
  envAD,
  fmVoice,
  loopNoise,
  makeVoice,
  noiseSweepVoice,
  pluckVoice,
  pushSources,
  riserVoice,
  stopVoice,
  subDropVoice,
  toneVoice,
  type Voice,
} from './synth';
import { DENIED, PRIORITY, VoiceAllocator, Ducker, type DuckSource } from './mixer';
import { MUSTER_MAX_LEAD_S, musterPulseOffsets } from './waves';

export interface CueSpec {
  id: string;
  family: FamilyName;
  priority: number;
  recipe: string;
  /** Per-cue concurrency hint from the data table (informational; the family cap governs). */
  voiceCap: number;
  /** ±fraction applied to playback rate (§12.3). */
  pitchVar: number;
  /** Linear peak gain, ≤ 0.5 per §12.6. */
  gain: number;
  /** Base pitch multiplier before variation. */
  rateBase: number;
  /** Minimum seconds between two plays of the same cue (coalescing, §12.7 acceptance). */
  minGapS: number;
  loop: boolean;
  /** R-WARN pulse count — the cues are told apart by it (§12.7). */
  pulses: number;
  /** Optional post filter for a cue variant (audio.player.hit lowpass 600 Hz). */
  lowpassHz: number;
  /** Optional layered recipe and its gain (elite death + R-CRIT at 0.5). */
  layerRecipe: string;
  layerGain: number;
  /** AMD-37 — recipe length in seconds; 0 means "the recipe's own default". */
  durS: number;
}

export type RecipeFn = (g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number, spec: CueSpec) => Voice;

// ---------------------------------------------------------------------------
// §12.4 recipe implementations
// ---------------------------------------------------------------------------

function rPluck(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  return pluckVoice(g, dest, t0, 660 * rate, 880 * rate, 0.02, gain, 2, 78, 1980 * rate, 0.15 * gain);
}

function rRasp(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const out = g.ctx.createGain();
  out.gain.value = 1;
  out.connect(dest);
  const v = makeVoice(out, t0 + 5 * 0.012 + 0.09);
  for (let k = 0; k < 5; k++) {
    const t = t0 + k * 0.012;
    const o = g.ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.value = 220 * rate;
    const bp = g.ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1200 * rate;
    bp.Q.value = 4;
    const eg = g.ctx.createGain();
    const end = envAD(eg.gain, t, gain * 0.55, 1, 55);
    o.connect(bp);
    bp.connect(eg);
    eg.connect(out);
    o.start(t);
    o.stop(end + 0.02);
    v.sources.push(o);
  }
  return v;
}

function rDrone(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const lp = g.ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 400;
  lp.Q.value = 0.9;
  const out = g.ctx.createGain();
  out.gain.setValueAtTime(EPS, t0);
  out.gain.linearRampToValueAtTime(gain, t0 + 0.08);
  lp.connect(out);
  out.connect(dest);
  const v = makeVoice(out, Infinity);
  const base = 110 * rate;
  const a = g.ctx.createOscillator();
  a.type = 'sawtooth';
  a.frequency.value = base;
  a.detune.value = -6;
  const b = g.ctx.createOscillator();
  b.type = 'sawtooth';
  b.frequency.value = base;
  b.detune.value = 6;
  a.connect(lp);
  b.connect(lp);
  a.start(t0);
  b.start(t0);
  v.sources.push(a, b);
  // Tremolo 4 Hz depth 0.08.
  v.sources.push(attachLfo(g, out.gain, t0, 4, gain * 0.08));
  v.tracking = lp.frequency;
  v.trackLo = 400;
  v.trackHi = 2400;
  return v;
}

function rHumcluster(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const out = g.ctx.createGain();
  out.gain.setValueAtTime(EPS, t0);
  out.gain.linearRampToValueAtTime(gain, t0 + 0.12);
  out.connect(dest);
  const v = makeVoice(out, Infinity);
  const freqs = HUM_FREQS;
  for (let i = 0; i < freqs.length; i++) {
    const o = g.ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = freqs[i] * rate;
    const og = g.ctx.createGain();
    og.gain.value = 0.34;
    o.connect(og);
    og.connect(out);
    o.start(t0);
    v.sources.push(o);
    // Chorus: 0.7 Hz, depth 4 cents.
    v.sources.push(attachLfo(g, o.detune, t0 + i * 0.11, 0.7, 4));
  }
  return v;
}

const HUM_FREQS = [220, 277, 330];

function rNova(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const v = subDropVoice(g, dest, t0, 180 * rate, 50 * rate, 0.25, gain, 1, 260);
  const shimmer = noiseSweepVoice(g, dest, t0, 3000, 5200, 0.4, gain * 0.35, 'highpass', 0.7);
  pushSources(v, shimmer);
  return v;
}

function rMineDeploy(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const v = toneVoice(g, dest, t0, 320 * rate, 'sine', gain, 1, 40);
  // Arm chirp completes at the 0.8 s arm time (§6.3.6): 120 ms glide starting at 0.68 s.
  const chirp = riserVoice(g, dest, t0 + 0.68, 600 * rate, 900 * rate, 0.12, gain * 0.6, 2, 118);
  pushSources(v, chirp);
  return v;
}

function rMineBlast(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const v = subDropVoice(g, dest, t0, 90 * rate, 40 * rate, 0.3, gain, 1, 320);
  const body = noiseSweepVoice(g, dest, t0, 800, 400, 0.32, gain * 0.5, 'lowpass', 0.9);
  pushSources(v, body);
  return v;
}

function rZap(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const v = noiseSweepVoice(g, dest, t0, 2500 * rate, 2500 * rate, 0.03, gain, 'bandpass', 8);
  const sq = toneVoice(g, dest, t0, 120 * rate, 'square', gain * 0.5, 1, 20);
  pushSources(v, sq);
  return v;
}

function rChirp(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  return riserVoice(g, dest, t0, 700 * rate, 1000 * rate, 0.09, gain, 2, 88);
}

function rTrill(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const out = g.ctx.createGain();
  out.gain.value = 1;
  out.connect(dest);
  const v = makeVoice(out, t0 + 0.14 + 0.1);
  const rates = TRILL_RATES;
  for (let i = 0; i < rates.length; i++) {
    const t = t0 + i * 0.07;
    const sub = riserVoice(g, out, t, 700 * rate * rates[i], 1000 * rate * rates[i], 0.09, gain * 0.8, 2, 88);
    pushSources(v, sub);
  }
  return v;
}

const TRILL_RATES = [1, 0.88, 0.76];

function rPour(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number, spec: CueSpec): Voice {
  const lp = g.ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 500 * rate;
  const out = g.ctx.createGain();
  lp.connect(out);
  out.connect(dest);
  const v = makeVoice(out, spec.loop ? Infinity : t0 + 0.45);
  if (spec.loop) {
    out.gain.setValueAtTime(EPS, t0);
    out.gain.linearRampToValueAtTime(gain, t0 + 0.15);
    const src = loopNoise(g, true, t0);
    src.connect(lp);
    v.sources.push(src);
  } else {
    envAD(out.gain, t0, gain, 20, 380);
    const src = g.ctx.createBufferSource();
    src.buffer = g.brown;
    src.loop = true;
    src.connect(lp);
    src.start(t0);
    src.stop(t0 + 0.45);
    v.sources.push(src);
  }
  // "Glug": 90 Hz sine wobbled at 6 Hz, depth 0.2.
  const glug = g.ctx.createOscillator();
  glug.type = 'sine';
  glug.frequency.value = 90 * rate;
  const gg = g.ctx.createGain();
  gg.gain.value = gain * 0.35;
  glug.connect(gg);
  gg.connect(out);
  glug.start(t0);
  if (!spec.loop) glug.stop(t0 + 0.45);
  v.sources.push(glug);
  v.sources.push(attachLfo(g, gg.gain, t0, 6, gain * 0.2));
  return v;
}

function rGong(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  return bellVoice(g, dest, t0, 180 * rate, gain, 600);
}

function rWhoosh(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const v = noiseSweepVoice(g, dest, t0, 300 * rate, 1200 * rate, 0.25, gain, 'bandpass', 1.6);
  const gulp = toneVoice(g, dest, t0, 70 * rate, 'sine', gain * 0.6, 5, 120);
  pushSources(v, gulp);
  return v;
}

function rPhaser(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const out = g.ctx.createGain();
  out.gain.setValueAtTime(EPS, t0);
  out.gain.linearRampToValueAtTime(gain, t0 + 0.2);
  out.connect(dest);
  const v = makeVoice(out, Infinity);
  const o = g.ctx.createOscillator();
  o.type = 'sawtooth';
  o.frequency.value = 110 * rate;
  let node: AudioNode = o;
  const centers = ALLPASS_CENTERS;
  for (let i = 0; i < centers.length; i++) {
    const ap = g.ctx.createBiquadFilter();
    ap.type = 'allpass';
    ap.frequency.value = centers[i];
    ap.Q.value = 0.8;
    node.connect(ap);
    node = ap;
    // LFO 0.33 Hz, ±40% of the center frequency, synced to the 6 s revolution.
    v.sources.push(attachLfo(g, ap.frequency, t0, 0.33, centers[i] * 0.4));
  }
  node.connect(out);
  o.start(t0);
  v.sources.push(o);
  return v;
}

const ALLPASS_CENTERS = [350, 700, 1400, 2800];

function rTick(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  return toneVoice(g, dest, t0, 1000 * rate, 'square', gain, 1, 14);
}

function rCrit(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  return fmVoice(g, dest, t0, 1200 * rate, 2700 * rate, 8, 0.15, gain, 1, 150);
}

function rThud(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number, spec: CueSpec): Voice {
  const cutoff = spec.lowpassHz > 0 ? spec.lowpassHz : 900;
  const v = noiseSweepVoice(g, dest, t0, cutoff, cutoff * 0.6, 0.05, gain * 0.7, 'lowpass', 0.9);
  const body = toneVoice(g, dest, t0, 150 * rate, 'sine', gain, 1, 40);
  pushSources(v, body);
  return v;
}

function rDeath(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const v = subDropVoice(g, dest, t0, 300 * rate, 80 * rate, 0.2, gain, 1, 200);
  const sparkle = noiseSweepVoice(g, dest, t0, 2000, 3600, 0.18, gain * 0.4, 'highpass', 0.8);
  pushSources(v, sparkle);
  return v;
}

function rPickupXp(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const a = toneVoice(g, dest, t0, 880 * rate, 'sine', gain, 2, 58);
  const b = toneVoice(g, dest, t0 + 0.06, 1320 * rate, 'sine', gain * 0.9, 2, 58);
  pushSources(a, b);
  return a;
}

function rPickupHp(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const out = g.ctx.createGain();
  const end = envAD(out.gain, t0, gain, 5, 250);
  out.connect(dest);
  const v = makeVoice(out, end + 0.05);
  const freqs = HP_TRIAD;
  for (let i = 0; i < freqs.length; i++) {
    const o = g.ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = freqs[i] * rate;
    const og = g.ctx.createGain();
    og.gain.value = 1 / freqs.length;
    o.connect(og);
    og.connect(out);
    o.start(t0);
    o.stop(end + 0.04);
    v.sources.push(o);
  }
  return v;
}

const HP_TRIAD = [440, 550, 660];
const LEVELUP_ARP = [523, 659, 784, 1046];

function rLevelup(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const out = g.ctx.createGain();
  out.gain.value = 1;
  out.connect(dest);
  const v = makeVoice(out, t0 + 0.4);
  for (let i = 0; i < LEVELUP_ARP.length; i++) {
    const t = t0 + i * 0.075;
    const n = toneVoice(g, out, t, LEVELUP_ARP[i] * rate, 'triangle', gain, 2, 120);
    pushSources(v, n);
  }
  const shimmer = noiseSweepVoice(g, out, t0, 5000, 7000, 0.3, gain * 0.3, 'highpass', 0.7);
  pushSources(v, shimmer);
  return v;
}

function rWarn(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number, spec: CueSpec): Voice {
  // Dual tone 520/780 Hz alternating at 4 Hz through a bandpass at 2 kHz Q2.
  // The reserved 1–3 kHz band (§12.6) is why warnings cut through at density.
  const bp = g.ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 2000;
  bp.Q.value = 2;
  const out = g.ctx.createGain();
  out.gain.value = 1;
  bp.connect(out);
  out.connect(dest);
  const pulses = spec.pulses > 0 ? spec.pulses : 3;
  const v = makeVoice(out, t0 + pulses * 0.25 + 0.1);
  for (let i = 0; i < pulses; i++) {
    const t = t0 + i * 0.25;
    const hi = i % 2 === 0;
    const o = g.ctx.createOscillator();
    o.type = 'square';
    o.frequency.value = (hi ? 780 : 520) * rate;
    const eg = g.ctx.createGain();
    const end = envAD(eg.gain, t, gain, 3, 180);
    o.connect(eg);
    eg.connect(bp);
    o.start(t);
    o.stop(end + 0.02);
    v.sources.push(o);
  }
  return v;
}

function rBossRoar(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const v = fmVoice(g, dest, t0, 60 * rate, 91 * rate, 12, 0.75, gain, 50, 700);
  const rumble = noiseSweepVoice(g, dest, t0, 300, 180, 0.7, gain * 0.45, 'lowpass', 0.8);
  pushSources(v, rumble);
  return v;
}

function rUiClick(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  return toneVoice(g, dest, t0, 2000 * rate, 'sine', gain, 1, 12);
}

function rUiConfirm(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const a = toneVoice(g, dest, t0, 1000 * rate, 'sine', gain, 1, 12);
  const b = toneVoice(g, dest, t0 + 0.06, 1500 * rate, 'sine', gain, 1, 12);
  pushSources(a, b);
  return a;
}

function rUiBack(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  return toneVoice(g, dest, t0, 750 * rate, 'sine', gain, 1, 12);
}

/** AMD-37 pulse reference pitch. C5; `rateBase` moves the whole telegraph's register. */
const MUSTER_PULSE_HZ = 523.25;
/** Ordinary muster: C5 → E5, a rising major third. Two pulses. */
const MUSTER_STEPS_2 = [1, 1.2599];
/** Boss muster: a rising minor third then a fourth, and `rateBase` drops it an octave+. */
const MUSTER_STEPS_3 = [1, 1.1892, 1.3348];
const MUSTER_OFFSETS: number[] = [];

/**
 * AMD-37 `R-MUSTER` — the incoming-wave telegraph. One voice, so it costs a single slot
 * in a family sub-cap and can never crowd out a warning.
 *
 * Three parts: a bandpass-swept noise bed rising 220 → 880 Hz, a sawtooth riser up one
 * octave, and `spec.pulses` countable blips landing on whole seconds before the boundary.
 * All of it sits under the reserved 1–3 kHz warning band (§12.6), so a hazard warning
 * still cuts straight through the telegraph.
 *
 * The envelope swells from 0.3 to full across `spec.durS` and peaks exactly at the wave
 * boundary, then clears in 350 ms as the onset takes over.
 */
function rMuster(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number, spec: CueSpec): Voice {
  const dur = spec.durS > 0 ? spec.durS : MUSTER_MAX_LEAD_S;
  const out = g.ctx.createGain();
  out.gain.setValueAtTime(0.3, t0);
  out.gain.linearRampToValueAtTime(1, t0 + dur);
  out.gain.linearRampToValueAtTime(EPS, t0 + dur + 0.35);
  out.connect(dest);
  const v = makeVoice(out, t0 + dur + 0.42);
  const bed = noiseSweepVoice(g, out, t0, 220 * rate, 880 * rate, dur, gain * 0.3, 'bandpass', 3.2);
  pushSources(v, bed);
  const riser = riserVoice(g, out, t0, 110 * rate, 220 * rate, dur, gain * 0.34, dur * 920, 240, 'sawtooth');
  pushSources(v, riser);
  const steps = spec.pulses >= 3 ? MUSTER_STEPS_3 : MUSTER_STEPS_2;
  const offsets = musterPulseOffsets(dur, steps.length, MUSTER_OFFSETS);
  for (let i = 0; i < offsets.length; i++) {
    const step = steps[steps.length - offsets.length + i];
    const p = toneVoice(g, out, t0 + offsets[i], MUSTER_PULSE_HZ * rate * step, 'triangle', gain * 0.55, 3, 150);
    pushSources(v, p);
  }
  return v;
}

/**
 * AMD-37 `R-WAVE-MARK` — wave-boundary punctuation. A 160 Hz body with a 480 Hz click on
 * top: ~110 ms, one voice, no RNG. Countable with your eyes on the arena.
 */
function rWaveMark(g: AudioGraph, dest: AudioNode, t0: number, rate: number, gain: number): Voice {
  const v = toneVoice(g, dest, t0, 160 * rate, 'sine', gain, 1, 90);
  const click = toneVoice(g, dest, t0, 480 * rate, 'square', gain * 0.28, 1, 22);
  pushSources(v, click);
  return v;
}

/** The three music layer cues are owned by `MusicDirector`; `play()` routes there instead. */
function rMusicManaged(g: AudioGraph, dest: AudioNode, t0: number): Voice {
  const out = g.ctx.createGain();
  out.gain.value = 0;
  out.connect(dest);
  return makeVoice(out, t0 + 0.01);
}

export const RECIPES: Readonly<Record<string, RecipeFn>> = {
  'R-PLUCK': rPluck,
  'R-RASP': rRasp,
  'R-DRONE': rDrone,
  'R-HUMCLUST': rHumcluster,
  'R-NOVA': rNova,
  'R-MINE-DEPLOY': rMineDeploy,
  'R-MINE-BLAST': rMineBlast,
  'R-ZAP': rZap,
  'R-CHIRP': rChirp,
  'R-TRILL': rTrill,
  'R-POUR': rPour,
  'R-GONG': rGong,
  'R-WHOOSH': rWhoosh,
  'R-PHASER': rPhaser,
  'R-TICK': rTick,
  'R-CRIT': rCrit,
  'R-THUD': rThud,
  'R-DEATH': rDeath,
  'R-PICKUP-XP': rPickupXp,
  'R-PICKUP-HP': rPickupHp,
  'R-LEVELUP': rLevelup,
  'R-WARN': rWarn,
  'R-BOSS-ROAR': rBossRoar,
  'R-UI-CLICK': rUiClick,
  'R-UI-CONFIRM': rUiConfirm,
  'R-UI-BACK': rUiBack,
  'R-MUSTER': rMuster,
  'R-WAVE-MARK': rWaveMark,
  'R-MUSIC-L1': rMusicManaged,
  'R-MUSIC-L2': rMusicManaged,
  'R-MUSIC-L3': rMusicManaged,
};

// ---------------------------------------------------------------------------
// Cue table
// ---------------------------------------------------------------------------

/** Cues that run as live loop graphs rather than one-shots (§12.4 last paragraph). */
export const LOOP_CUES: readonly string[] = [
  'audio.weapons.beam-loop',
  'audio.weapons.orbiter-hum',
  'audio.weapons.pool-sizzle',
  'audio.weapons.sweep-hum',
  'audio.ambient.trench',
  'audio.music.layer-calm',
  'audio.music.layer-pulse',
  'audio.music.layer-surge',
];

/** Coalescing floors read off the §12.7 acceptance strings (max rate → min gap). */
const MIN_GAP_S: Readonly<Record<string, number>> = {
  'audio.weapons.lance-hit': 0.05,
  'audio.weapons.krill-hit': 0.07,
  'audio.weapons.beam-tick': 0.25,
  'audio.weapons.orbiter-tick': 0.1667,
  'audio.weapons.sweep-tick': 0.2,
  'audio.weapons.maw-devour': 0.06,
  'audio.weapons.chain-zap': 0.03,
  'audio.crit.strike': 0.05,
  'audio.enemy.hit': 0.125,
  'audio.enemy.death-standard': 0.1667,
  'audio.pickup.xp': 0.1,
  'audio.boss.phase': 2.0,
  'audio.warning.low-hp': 5.0,
  'audio.ui.move': 0.11,
};

/** Per-voice peak gains. §12.6 caps any single voice at 0.5. */
const CUE_GAIN: Readonly<Record<string, number>> = {
  'audio.weapons.lance-fire': 0.3,
  'audio.weapons.lance-hit': 0.26,
  'audio.weapons.krill-volley': 0.26,
  'audio.weapons.krill-hit': 0.22,
  'audio.weapons.beam-loop': 0.2,
  'audio.weapons.beam-tick': 0.18,
  'audio.weapons.orbiter-hum': 0.18,
  'audio.weapons.orbiter-tick': 0.16,
  'audio.weapons.nova-blast': 0.4,
  'audio.weapons.mine-deploy': 0.24,
  'audio.weapons.mine-blast': 0.42,
  'audio.weapons.chain-zap': 0.28,
  'audio.weapons.scion-spawn': 0.2,
  'audio.weapons.scion-dive': 0.24,
  'audio.weapons.pool-cast': 0.28,
  'audio.weapons.pool-sizzle': 0.16,
  'audio.weapons.shroud-block': 0.4,
  'audio.weapons.shroud-break': 0.42,
  'audio.weapons.maw-lunge': 0.32,
  'audio.weapons.maw-devour': 0.2,
  'audio.weapons.sweep-hum': 0.16,
  'audio.weapons.sweep-tick': 0.16,
  'audio.crit.strike': 0.34,
  'audio.enemy.hit': 0.24,
  'audio.enemy.death-standard': 0.28,
  'audio.enemy.death-elite': 0.4,
  'audio.pickup.xp': 0.18,
  'audio.pickup.health': 0.34,
  'audio.pickup.powerup': 0.34,
  'audio.levelup.fanfare': 0.44,
  'audio.levelup.select': 0.3,
  'audio.boss.intro': 0.5,
  'audio.boss.phase': 0.44,
  'audio.boss.telegraph': 0.4,
  'audio.warning.hazard': 0.46,
  'audio.warning.boss-spawn': 0.46,
  'audio.warning.low-hp': 0.42,
  'audio.player.hit': 0.5,
  'audio.player.dash': 0.26,
  'audio.ui.move': 0.18,
  'audio.ui.confirm': 0.24,
  'audio.ui.back': 0.22,
  'audio.ui.reroll': 0.24,
  'audio.ui.pause': 0.24,
  'audio.music.layer-calm': 0.3,
  'audio.music.layer-pulse': 0.3,
  'audio.music.layer-surge': 0.3,
  'audio.ambient.trench': 0.1,
};

/** Per-cue tuning that the data table encodes in prose rather than fields. */
const CUE_TWEAKS: Readonly<Record<string, Partial<CueSpec>>> = {
  // §12.7 — warnings are told apart by pulse count: boss-spawn 2, hazard 3.
  'audio.warning.boss-spawn': { pulses: 2 },
  'audio.warning.hazard': { pulses: 3 },
  'audio.warning.low-hp': { pulses: 3 },
  'audio.boss.telegraph': { pulses: 2 },
  // "gain 1.4× R-THUD base + lowpass 600 Hz variant" (audio.player.hit acceptance).
  'audio.player.hit': { lowpassHz: 600 },
  // "4-note arp at 0.6× rate distinguishes it from audio.levelup.fanfare".
  'audio.pickup.powerup': { rateBase: 0.6 },
  // "layered with R-CRIT partial at 0.5 gain for weight".
  'audio.enemy.death-elite': { layerRecipe: 'R-CRIT', layerGain: 0.5 },
  // "short whoosh at 0.6× duration" — a faster sweep reads shorter.
  'audio.player.dash': { rateBase: 1.35 },
};

/**
 * AMD-37 wave-arc cues. Declared in code rather than in `content/audio-cues.json`
 * because §12.7's family inventory predates waves; the data table stays the §12 contract
 * and these three are an additive presentation layer over it. `WAVE_CUE_IDS` is what the
 * §12.8 acceptance test counts separately from the 48 data-table cues.
 *
 * Family choice is a §12.2 safety property, not a taste call. The ordinary telegraph and
 * the boundary mark are `ui` (priority 2), the boss telegraph is `boss` (priority 3).
 * Neither class can ever steal from `warnings` (5): `pickVictimGlobal` refuses any victim
 * whose priority is ≥ the requester's, and a sub-cap steal only touches its own family.
 */
export const WAVE_CUE_IDS: readonly string[] = ['audio.wave.mark', 'audio.wave.muster', 'audio.wave.muster-boss'];

const WAVE_CUE_SPECS: readonly CueSpec[] = [
  {
    id: 'audio.wave.mark',
    family: 'ui',
    priority: PRIORITY.ui,
    recipe: 'R-WAVE-MARK',
    voiceCap: 1,
    pitchVar: 0, // fixed, like warnings — the beat must be instantly recognizable
    gain: 0.26,
    rateBase: 1,
    minGapS: 1,
    loop: false,
    pulses: 0,
    lowpassHz: 0,
    layerRecipe: '',
    layerGain: 0,
    durS: 0,
  },
  {
    id: 'audio.wave.muster',
    family: 'ui',
    priority: PRIORITY.ui,
    recipe: 'R-MUSTER',
    voiceCap: 1,
    pitchVar: 0,
    gain: 0.3,
    rateBase: 1,
    minGapS: 4,
    loop: false,
    pulses: 2,
    lowpassHz: 0,
    layerRecipe: '',
    layerGain: 0,
    durS: 0,
  },
  {
    // A full octave below the ordinary telegraph, one extra pulse, and an R-BOSS-ROAR
    // growl underneath. An octave (not more) because the pulses have to survive a phone
    // speaker: at 0.5 they sit at 262/311/349 Hz, still unmistakably the lower cue.
    // It also routes to the `boss` bus, which ducks weapons −3 dB (§12.5) — the mix
    // opens up for it the way a boss cue does.
    id: 'audio.wave.muster-boss',
    family: 'boss',
    priority: PRIORITY.boss,
    recipe: 'R-MUSTER',
    voiceCap: 1,
    pitchVar: 0,
    gain: 0.44,
    rateBase: 0.5,
    minGapS: 4,
    loop: false,
    pulses: 3,
    lowpassHz: 0,
    layerRecipe: 'R-BOSS-ROAR',
    layerGain: 0.35,
    durS: 0,
  },
];

/** §12.3 — parse "+/-5%" / "0%" / prose variants into a fraction. */
export function parsePitchVariation(text: string | undefined): number {
  if (!text) return 0;
  const m = /\+\/-\s*(\d+(?:\.\d+)?)\s*%/.exec(text);
  if (!m) return 0;
  return Number(m[1]) / 100;
}

function isFamilyName(v: string): v is FamilyName {
  for (let i = 0; i < FAMILY_NAMES.length; i++) if (FAMILY_NAMES[i] === v) return true;
  return false;
}

function buildCueTable(): Map<string, CueSpec> {
  const map = new Map<string, CueSpec>();
  for (let i = 0; i < AUDIO_CUES.length; i++) {
    const raw = AUDIO_CUES[i] as AudioCueDef & {
      family?: string;
      priority_class?: number;
      voice_cap?: number;
      pitch_variation?: string;
      recipe_ref?: string;
    };
    const famRaw = typeof raw.family === 'string' ? raw.family : 'ui';
    const family: FamilyName = isFamilyName(famRaw) ? famRaw : 'ui';
    const spec: CueSpec = {
      id: raw.id,
      family,
      priority: typeof raw.priority_class === 'number' ? raw.priority_class : PRIORITY[family],
      recipe: typeof raw.recipe_ref === 'string' ? raw.recipe_ref : '',
      voiceCap: typeof raw.voice_cap === 'number' ? raw.voice_cap : 1,
      pitchVar: parsePitchVariation(raw.pitch_variation),
      gain: CUE_GAIN[raw.id] ?? 0.25,
      rateBase: 1,
      minGapS: MIN_GAP_S[raw.id] ?? 0,
      loop: LOOP_CUES.indexOf(raw.id) >= 0,
      pulses: 0,
      lowpassHz: 0,
      layerRecipe: '',
      layerGain: 0,
      durS: 0,
    };
    const tweak = CUE_TWEAKS[raw.id];
    if (tweak) Object.assign(spec, tweak);
    map.set(raw.id, spec);
  }
  for (let i = 0; i < WAVE_CUE_SPECS.length; i++) map.set(WAVE_CUE_SPECS[i].id, WAVE_CUE_SPECS[i]);
  return map;
}

export const CUES: ReadonlyMap<string, CueSpec> = buildCueTable();

/** §12.8 acceptance 1 — every cue must cite an implemented R-* recipe. */
export function missingRecipes(): string[] {
  const out: string[] = [];
  CUES.forEach((spec) => {
    if (!spec.recipe || !RECIPES[spec.recipe]) out.push(spec.id);
  });
  return out;
}

/** §12.2 — the nine SFX family sub-caps must sum to 29. */
export function familyCueCounts(): Record<string, number> {
  const out: Record<string, number> = {};
  CUES.forEach((spec) => {
    out[spec.family] = (out[spec.family] ?? 0) + 1;
  });
  return out;
}

// ---------------------------------------------------------------------------
// Player
// ---------------------------------------------------------------------------

export interface PlayOptions {
  pitch?: number;
  gain?: number;
  /** One-off layered recipe (evolution picks layer R-GONG at 0.4 gain, §12.7). */
  layerRecipe?: string;
  layerGain?: number;
  /** AMD-37 — overrides `CueSpec.durS` for one play (the muster's landing window). */
  durS?: number;
}

export interface RandomSource {
  next(): number;
}

/** Fallback pitch source used before a world is bound. Never a gameplay stream. */
class LocalRandom implements RandomSource {
  private s = 0x1b873593;
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
}

/**
 * Preallocated override for a one-off duration. `play` runs synchronously and the recipe
 * reads the spec before returning, so a single scratch object is safe and keeps the
 * event handlers allocation-free.
 */
const DUR_SCRATCH: CueSpec = {
  id: '',
  family: 'ui',
  priority: 2,
  recipe: '',
  voiceCap: 1,
  pitchVar: 0,
  gain: 0.25,
  rateBase: 1,
  minGapS: 0,
  loop: false,
  pulses: 0,
  lowpassHz: 0,
  layerRecipe: '',
  layerGain: 0,
  durS: 0,
};

function withDuration(spec: CueSpec, durS: number): CueSpec {
  Object.assign(DUR_SCRATCH, spec);
  DUR_SCRATCH.durS = durS;
  return DUR_SCRATCH;
}

interface LoopHandle {
  voice: Voice;
  slot: number;
  voiceId: number;
  ducked: boolean;
  baseGain: number;
}

/**
 * Turns cue ids into voices, subject to the §12.2 cap and the §12.5 ducking rules.
 * Holds no sim state and never writes to the world.
 */
export class SfxPlayer {
  private graph: AudioGraph | null = null;
  readonly allocator = new VoiceAllocator();
  private readonly voices: (Voice | null)[] = [];
  private readonly voiceIds: number[] = [];
  private readonly lastPlayAt = new Map<string, number>();
  private readonly loops = new Map<string, LoopHandle>();
  private readonly weaponsDucker = new Ducker(1);
  rng: RandomSource = new LocalRandom();
  /** Sim tick, mirrored from the world for the steal log; never written back. */
  tick = 0;

  constructor() {
    for (let i = 0; i < this.allocator.slots.length; i++) {
      this.voices.push(null);
      this.voiceIds.push(0);
    }
  }

  attach(g: AudioGraph): void {
    this.graph = g;
    this.weaponsDucker.setBaseGain(g.families.weapons.gain.value);
  }

  detach(): void {
    this.stopAll();
    this.graph = null;
  }

  get ducker(): Ducker {
    return this.weaponsDucker;
  }

  /** §12.3 pitch variation. Consumes the audio stream only. */
  private pitchRate(spec: CueSpec, opts?: PlayOptions): number {
    let rate = spec.rateBase;
    if (opts && typeof opts.pitch === 'number' && opts.pitch > 0) rate *= opts.pitch;
    if (spec.pitchVar > 0) {
      const draw = this.rng.next() * 2 - 1;
      rate *= 1 + draw * spec.pitchVar;
    }
    return rate <= 0 ? 1 : rate;
  }

  play(cueId: string, opts?: PlayOptions): boolean {
    const g = this.graph;
    if (!g) return false;
    return this.playAt(cueId, g.ctx.currentTime, opts);
  }

  /**
   * AMD-37 — plays a cue at an explicit AudioContext time. `when` in the past falls back
   * to "now", so the worst case is the old behaviour. This is what lets the muster
   * telegraph land on the wave boundary sample-accurately instead of on the next rAF:
   * every oscillator, envelope segment and pulse is placed once on the audio timeline.
   */
  playAt(cueId: string, when: number, opts?: PlayOptions): boolean {
    const g = this.graph;
    if (!g) return false;
    const spec = CUES.get(cueId);
    if (!spec) return false;
    if (spec.recipe === 'R-MUSIC-L1' || spec.recipe === 'R-MUSIC-L2' || spec.recipe === 'R-MUSIC-L3') return false;
    if (spec.loop) return this.startLoop(cueId);

    const now = g.ctx.currentTime;
    const at = when > now ? when : now;
    if (spec.minGapS > 0) {
      const last = this.lastPlayAt.get(cueId);
      if (last !== undefined && at - last < spec.minGapS) return false;
    }

    const fn = RECIPES[spec.recipe];
    if (!fn) return false;
    const gainScale = opts && typeof opts.gain === 'number' ? Math.max(0, opts.gain) : 1;
    const peak = Math.min(0.5, spec.gain * gainScale);
    const rate = this.pitchRate(spec, opts);
    const useSpec = opts && opts.durS !== undefined && opts.durS > 0 ? withDuration(spec, opts.durS) : spec;
    // Duration estimate for the allocator; the real voice reports its own endAt below.
    const res = this.allocator.request(now, cueId, spec.family, peak, at + 1.2, false, this.tick);
    if (res.index === DENIED) return false;
    if (res.stolen !== DENIED) this.fadeSlot(g, res.stolen, now);

    const dest = g.families[spec.family];
    const voice = fn(g, dest, at, rate, peak, useSpec);
    const layerId = opts && opts.layerRecipe ? opts.layerRecipe : spec.layerRecipe;
    const layerAmt = opts && opts.layerRecipe ? (opts.layerGain ?? 0.4) : spec.layerGain;
    if (layerId && RECIPES[layerId] && layerAmt > 0) {
      const layer = RECIPES[layerId](g, dest, at, rate, peak * layerAmt, useSpec);
      pushSources(voice, layer);
    }
    this.voices[res.index] = voice;
    this.voiceIds[res.index] = this.allocator.slots[res.index].voiceId;
    this.allocator.slots[res.index].endAt = voice.endAt;
    this.lastPlayAt.set(cueId, at);
    this.applyDucking(g, spec, at);
    return true;
  }

  /** §12.4 — starts a loop cue if it is not already running. Idempotent. */
  startLoop(cueId: string): boolean {
    const g = this.graph;
    if (!g) return false;
    if (this.loops.has(cueId)) return true;
    const spec = CUES.get(cueId);
    if (!spec || !spec.loop) return false;
    const fn = RECIPES[spec.recipe];
    if (!fn) return false;
    const now = g.ctx.currentTime;
    const res = this.allocator.request(now, cueId, spec.family, spec.gain, Infinity, true, this.tick);
    if (res.index === DENIED) return false;
    if (res.stolen !== DENIED) this.fadeSlot(g, res.stolen, now);
    const voice = fn(g, g.families[spec.family], now, 1, spec.gain, spec);
    this.voices[res.index] = voice;
    this.voiceIds[res.index] = this.allocator.slots[res.index].voiceId;
    this.loops.set(cueId, {
      voice,
      slot: res.index,
      voiceId: this.allocator.slots[res.index].voiceId,
      ducked: false,
      baseGain: spec.gain,
    });
    return true;
  }

  stopLoop(cueId: string): void {
    const g = this.graph;
    const h = this.loops.get(cueId);
    if (!h) return;
    this.loops.delete(cueId);
    if (g) {
      stopVoice(g, h.voice, g.ctx.currentTime, 0.08);
      disconnectVoice(h.voice);
    }
    this.allocator.release(h.slot, h.voiceId);
    if (this.voiceIds[h.slot] === h.voiceId) {
      this.voices[h.slot] = null;
      this.voiceIds[h.slot] = 0;
    }
  }

  isLooping(cueId: string): boolean {
    return this.loops.has(cueId);
  }

  /** §12.4 R-DRONE / R-MUSIC-L2 — loop cutoffs track music intensity I. */
  setIntensity(I: number): void {
    const g = this.graph;
    if (!g) return;
    const now = g.ctx.currentTime;
    this.loops.forEach((h) => {
      const p = h.voice.tracking;
      if (!p) return;
      p.cancelScheduledValues(now);
      p.setValueAtTime(p.value, now);
      p.linearRampToValueAtTime(h.voice.trackLo + (h.voice.trackHi - h.voice.trackLo) * I, now + 0.4);
    });
  }

  /**
   * §6.3.9 / §12.5 — `audio.weapons.pool-sizzle` self-ducks −6 dB whenever ≥ 8 enemy
   * voices are live. Engages within 100 ms of the threshold crossing.
   */
  updateSelfDuck(): void {
    const g = this.graph;
    if (!g) return;
    const h = this.loops.get('audio.weapons.pool-sizzle');
    if (!h) return;
    const dense = this.allocator.familyCount('enemy') >= 8;
    if (dense === h.ducked) return;
    h.ducked = dense;
    const now = g.ctx.currentTime;
    const target = dense ? h.baseGain * 0.5012 : h.baseGain; // −6 dB
    h.voice.gain.gain.cancelScheduledValues(now);
    h.voice.gain.gain.setValueAtTime(h.voice.gain.gain.value, now);
    h.voice.gain.gain.linearRampToValueAtTime(target, now + 0.08);
  }

  /** §12.5 sidechain: warnings/boss/player_damage duck the weapons bus. */
  private applyDucking(g: AudioGraph, spec: CueSpec, now: number): void {
    let source: DuckSource | null = null;
    let duration = 0;
    if (spec.family === 'warnings') {
      source = 'warnings';
      duration = 0.6;
    } else if (spec.family === 'boss') {
      source = 'boss';
      duration = spec.id === 'audio.boss.intro' ? 3.0 : 0.6;
    } else if (spec.family === 'player_damage' || spec.priority === 4) {
      source = 'player_damage';
      duration = 0.25;
    }
    if (!source) return;
    this.weaponsDucker.apply(g, g.families.weapons.gain, source, now, duration);
  }

  /**
   * §8.9 recovery envelope ≤ 0.70 ducks the weapons bus −3 dB (§12.6 rule 2).
   *
   * `durationS` is the hold, not the total: the duck releases that long after the last
   * arm. AMD-37 re-arms it at 10 Hz with a short hold through the lull, so the weapons
   * bus comes back up ~0.5 s into the onset instead of hanging over the swell.
   */
  setRecoveryDuck(active: boolean, durationS = 1.2): void {
    const g = this.graph;
    if (!g) return;
    if (!active) return;
    this.weaponsDucker.apply(g, g.families.weapons.gain, 'recovery', g.ctx.currentTime, durationS);
  }

  private fadeSlot(g: AudioGraph, slot: number, now: number): void {
    const v = this.voices[slot];
    if (!v) return;
    stopVoice(g, v, now, 0.02); // §12.2 rule (5): 20 ms, never a hard cut
    this.voices[slot] = null;
    this.voiceIds[slot] = 0;
  }

  /** Frees finished voices. Cheap ascending pass; called from the engine poll. */
  collect(): void {
    const g = this.graph;
    if (!g) return;
    const now = g.ctx.currentTime;
    for (let i = 0; i < this.voices.length; i++) {
      const v = this.voices[i];
      if (!v || v.endAt > now) continue;
      disconnectVoice(v);
      this.voices[i] = null;
      this.voiceIds[i] = 0;
    }
    this.allocator.prune(now);
  }

  stopAll(): void {
    const g = this.graph;
    if (g) {
      const now = g.ctx.currentTime;
      for (let i = 0; i < this.voices.length; i++) {
        const v = this.voices[i];
        if (!v) continue;
        stopVoice(g, v, now, 0.05);
        disconnectVoice(v);
        this.voices[i] = null;
        this.voiceIds[i] = 0;
      }
    }
    this.loops.clear();
    this.lastPlayAt.clear();
    this.allocator.reset();
  }
}
